import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/** Class making a field span both columns of a {@link FormSection} body. */
export const FORM_SECTION_FULL_WIDTH_CLASSNAME = 'lg:col-span-2';

/**
 * Wraps a field that must span both columns of a {@link FormSection} body
 * (address block, rhythm grid, signature…).
 *
 * @param props.children - the full-width content
 * @param props.className - extra classes for the wrapper
 * @returns the spanning wrapper
 */
export const FormSectionFullWidth = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div className={cn(FORM_SECTION_FULL_WIDTH_CLASSNAME, className)}>
    {children}
  </div>
);

/**
 * A titled group of related fields. The body is a single column on small
 * screens and two columns from `lg` up; sections are separated by a subtle
 * top border.
 *
 * @param props.title - translated section title
 * @param props.description - optional translated helper text under the title
 * @param props.children - the section fields
 * @param props.className - extra classes for the section
 * @returns the section
 */
export const FormSection = ({
  title,
  description,
  children,
  className,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
}) => (
  <section
    className={cn(
      'grid gap-4 border-border border-t pt-5 first:border-t-0 first:pt-0',
      className,
    )}
  >
    <div className="grid gap-1">
      <h3 className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
        {title}
      </h3>
      {description && (
        <p className="text-muted-foreground text-sm">{description}</p>
      )}
    </div>
    <div className="grid gap-4 lg:grid-cols-2">{children}</div>
  </section>
);
